import { Injectable } from '@angular/core';
import { IShipment } from '../interfaces/IShipment';
import { IShipmentHistory } from '../interfaces/IShipmentHistory';
import { IDeliveryStatus } from '../interfaces/IDeliveryStatus';
import { IJustification } from '../interfaces/IJustification';

const DB_NAME = 'TGCSDriverViewDB';
const DB_VERSION = 2;
const STORE_SHIPMENTS = 'shipments';
const STORE_SHIPMENT_UPDATES = 'shipmentUpdates';
const STORE_DELIVERY_STATUS = 'deliveryStatus';
const DELIVERY_STATUS_LIST_KEY = 'deliveryStatusList';
const STORE_JUSTIFICATION = 'justifications'
const JUSTIFICATION_KEY = 'justificationList'

@Injectable({
  providedIn: 'root'
})
export class DbService {
  constructor() {}

  private openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains(STORE_SHIPMENTS)) {
          db.createObjectStore(STORE_SHIPMENTS, { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains(STORE_SHIPMENT_UPDATES)) {
          db.createObjectStore(STORE_SHIPMENT_UPDATES, {
            keyPath: 'queuedId',
            autoIncrement: true
          });
        }

        if (!db.objectStoreNames.contains(STORE_DELIVERY_STATUS)) {
          db.createObjectStore(STORE_DELIVERY_STATUS, { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains(STORE_JUSTIFICATION)) {
          db.createObjectStore(STORE_JUSTIFICATION, { keyPath: 'key' });
        }

        
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      request.onblocked = () => console.warn('IndexedDB open blocked.');
    });
  }

  private transaction<T>(storeName: string, mode: IDBTransactionMode, callback: (store: IDBObjectStore) => IDBRequest<any> | Promise<T>): Promise<T> {
    return this.openDb().then(db => {
      return new Promise<T>((resolve, reject) => {
        const tx = db.transaction(storeName, mode);
        const store = tx.objectStore(storeName);

        const resultPromise = callback(store);


        if (resultPromise && 'onsuccess' in resultPromise && 'onerror' in resultPromise) {
          const request = resultPromise as IDBRequest<any>;
          request.onsuccess = () => resolve(request.result as T);
          request.onerror = () => reject(request.error);
        } else {

          (resultPromise as Promise<T>).then(resolve, reject);
        }

        tx.oncomplete = () => db.close();
        tx.onerror = () => reject(tx.error);
      });
    });
  }



  saveShipments(shipment: IShipment): Promise<void> {
    return this.openDb().then(db => {
      return new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_SHIPMENTS, 'readwrite');
        const store = tx.objectStore(STORE_SHIPMENTS);

        store.add(shipment);

        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      });
    });
  }

  getShipments(): Promise<IShipment[]> {
    return this.transaction<IShipment[]>(STORE_SHIPMENTS, 'readonly', store => store.getAll())
      .then((items: any[]) => items as IShipment[])
      .catch(() => []);
  }

  getShipmentById(shipmentId: number | string): Promise<IShipment | null> {
    return this.transaction<IShipment | null>(STORE_SHIPMENTS, 'readonly', store => store.get(shipmentId))
      .then((record: any) => (record ? record as IShipment : null))
      .catch(() => null);
  }

  updateShipment(shipment: IShipment): Promise<void> {
    return this.transaction<void>(STORE_SHIPMENTS, 'readwrite', store => store.put(shipment));
  }

  deleteShipmentById(shipmentId: number | string): Promise<void> {
    return this.transaction<void>(STORE_SHIPMENTS, 'readwrite', store => store.delete(shipmentId));
  }

  clearShipments(): Promise<void> {
    return this.transaction<void>(STORE_SHIPMENTS, 'readwrite', store => store.clear());
  }

  saveDeliveryStatusList(statuses: IDeliveryStatus[]): Promise<void> {
    return this.transaction<void>(STORE_DELIVERY_STATUS, 'readwrite', store =>
      store.put({ key: DELIVERY_STATUS_LIST_KEY, statuses })
    );
  }

  getDeliveryStatusList(): Promise<IDeliveryStatus[]> {
    return this.transaction<any>(STORE_DELIVERY_STATUS, 'readonly', store => store.get(DELIVERY_STATUS_LIST_KEY))
      .then(record => (record && record.statuses ? (record.statuses as IDeliveryStatus[]) : []))
      .catch(() => []);
  }


  saveJustificationList(justifications: IJustification[]): Promise<void> {
    return this.transaction<void>(STORE_JUSTIFICATION, 'readwrite', store =>
      store.put({ key: JUSTIFICATION_KEY, justifications })
    );
  }

  getJustificationList(): Promise<IJustification[]> {
    return this.transaction<any>(STORE_JUSTIFICATION, 'readonly', store => store.get(JUSTIFICATION_KEY))
      .then(record => (record && record.justifications ? (record.justifications as IJustification[]) : []))
      .catch(() => []);
  }


  queueShipmentUpdate(update: IShipmentHistory): Promise<number> {
    return this.transaction<number>(STORE_SHIPMENT_UPDATES, 'readwrite', store => store.add({ data: update }))
      .then(key => Number(key));
  }

  getShipmentUpdates(): Promise<Array<{ queuedId: number; data: IShipmentHistory }>> {
    return this.transaction<any[]>(STORE_SHIPMENT_UPDATES, 'readonly', store => store.getAll())
      .then((items: any[]) => items.map(i => ({ queuedId: i.queuedId, data: i.data as IShipmentHistory })))
      .catch(() => []);
  }

  removeShipmentUpdate(queuedId: number): Promise<void> {
    return this.transaction<void>(STORE_SHIPMENT_UPDATES, 'readwrite', store => store.delete(queuedId));
  }

  clearShipmentUpdates(): Promise<void> {
    return this.transaction<void>(STORE_SHIPMENT_UPDATES, 'readwrite', store => store.clear());
  }
}
